import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from './ui/button';
import { Mic, Square, Upload, Play, Pause, RotateCcw, CheckCircle } from 'lucide-react';

const VoiceRecorder = ({ onVoiceReady, disabled }) => {
  const [mode, setMode] = useState('idle'); // idle, recording, recorded, uploading, done
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const startRecording = useCallback(async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setMode('recorded');
        stream.getTracks().forEach(t => t.stop());
      };

      mediaRecorder.start(250);
      setMode('recording');
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch (err) {
      setError('Microphone access denied. Please allow microphone access or upload a file instead.');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleFileUpload = (e) => {
    setError('');
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/webm', 'audio/ogg', 'audio/mp4', 'audio/x-m4a'];
    if (!validTypes.some(t => file.type.includes(t.split('/')[1]))) {
      setError('Please upload an MP3, WAV, or M4A audio file.');
      return;
    }
    setAudioBlob(file);
    const url = URL.createObjectURL(file);
    setAudioUrl(url);
    setMode('recorded');
    setRecordingTime(Math.round(file.size / 16000)); // rough estimate
  };

  const handleReset = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setMode('idle');
    setRecordingTime(0);
    setIsPlaying(false);
    setError('');
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSubmit = async () => {
    if (!audioBlob) return;
    setMode('uploading');

    try {
      const reader = new FileReader();
      const base64 = await new Promise((resolve, reject) => {
        reader.onload = () => {
          const base64Data = reader.result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });
      onVoiceReady(base64);
      setMode('done');
    } catch (err) {
      setError('Failed to process audio. Please try again.');
      setMode('recorded');
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="space-y-6" data-testid="voice-recorder">
      {error && (
        <div className="bg-red-50 text-red-600 text-sm rounded-xl p-4" data-testid="voice-error">
          {error}
        </div>
      )}

      {mode === 'done' && (
        <div className="text-center py-8 space-y-3" data-testid="voice-done">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <p className="text-lg font-semibold text-gray-800">Voice Sample Ready</p>
          <p className="text-sm text-gray-500">Your voice has been captured and will be used to narrate your audiobook.</p>
        </div>
      )}

      {mode === 'idle' && (
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-6">
              Record yourself speaking naturally for 30-60 seconds. Read a passage, tell a story, or simply talk about your day.
              This will be used to clone your voice for the audiobook narration.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={startRecording}
              disabled={disabled}
              className="group flex flex-col items-center gap-3 p-8 rounded-2xl border-2 border-dashed border-pink-300 hover:border-pink-500 hover:bg-pink-50/50 transition-all"
              data-testid="start-recording-btn"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Mic className="w-7 h-7 text-white" />
              </div>
              <span className="font-semibold text-gray-800">Record Voice</span>
              <span className="text-xs text-gray-500">Use your microphone</span>
            </button>

            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              className="group flex flex-col items-center gap-3 p-8 rounded-2xl border-2 border-dashed border-purple-300 hover:border-purple-500 hover:bg-purple-50/50 transition-all"
              data-testid="upload-voice-btn"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Upload className="w-7 h-7 text-white" />
              </div>
              <span className="font-semibold text-gray-800">Upload Audio</span>
              <span className="text-xs text-gray-500">MP3, WAV, or M4A</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>
        </div>
      )}

      {mode === 'recording' && (
        <div className="text-center space-y-6 py-4" data-testid="recording-state">
          <div className="relative inline-flex items-center justify-center">
            <div className="absolute w-28 h-28 bg-red-100 rounded-full animate-ping opacity-30" />
            <div className="w-24 h-24 bg-gradient-to-br from-red-400 to-rose-500 rounded-full flex items-center justify-center shadow-xl">
              <Mic className="w-10 h-10 text-white animate-pulse" />
            </div>
          </div>
          <div>
            <p className="text-3xl font-mono font-bold text-gray-800" data-testid="recording-timer">{formatTime(recordingTime)}</p>
            <p className="text-sm text-gray-500 mt-1">Recording... speak naturally</p>
            {recordingTime < 30 && (
              <p className="text-xs text-amber-600 mt-2">Please record at least 30 seconds for best results</p>
            )}
          </div>
          <Button
            onClick={stopRecording}
            className="bg-red-500 hover:bg-red-600 text-white px-8 py-5 rounded-full border-0 text-base"
            data-testid="stop-recording-btn"
          >
            <Square className="w-5 h-5 mr-2" />
            Stop Recording
          </Button>
        </div>
      )}

      {mode === 'recorded' && (
        <div className="space-y-5" data-testid="recorded-state">
          <div className="bg-gray-50 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <button
                onClick={togglePlay}
                className="w-14 h-14 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform flex-shrink-0"
                data-testid="play-preview-btn"
              >
                {isPlaying ? <Pause className="w-6 h-6 text-white" /> : <Play className="w-6 h-6 text-white ml-0.5" />}
              </button>
              <div className="flex-1">
                <p className="font-semibold text-gray-800">Voice Sample</p>
                <p className="text-sm text-gray-500">Duration: ~{formatTime(recordingTime)}</p>
              </div>
              <button onClick={handleReset} className="text-gray-400 hover:text-gray-600" data-testid="reset-recording-btn">
                <RotateCcw className="w-5 h-5" />
              </button>
            </div>
            {audioUrl && (
              <audio
                ref={audioRef}
                src={audioUrl}
                onEnded={() => setIsPlaying(false)}
                className="hidden"
              />
            )}
          </div>

          <Button
            onClick={handleSubmit}
            disabled={disabled}
            className="w-full btn-primary text-white py-5 rounded-full border-0 text-base font-semibold"
            data-testid="confirm-voice-btn"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            Use This Voice Sample
          </Button>
        </div>
      )}

      {mode === 'uploading' && (
        <div className="text-center py-8 space-y-4" data-testid="uploading-state">
          <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin mx-auto" />
          <p className="text-gray-600">Processing your voice sample...</p>
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;
